import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Alert,
  ListSubheader,
  ToggleButton,
  ToggleButtonGroup,
  Tabs,
  Tab,
} from '@mui/material';
import { Download as DownloadIcon, Info as InfoIcon } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { getAuditLogs, getAuditLogById, exportAuditLogsCsv } from '../services/auditLog.service';
import { projectService } from '../services/projectService';
import { drugService } from '../services/drugService';
import { projectListPrimaryLabel, projectListSecondaryLabel } from '../utils/projectIdentityFields';
import PageHeader from '../components/common/PageHeader';
import EmptyState from '../components/common/EmptyState';
import LoadingState from '../components/common/LoadingState';
import { IND_ENABLED } from '../config/env';


// Two trails over the same AuditLog collection, split server-side by the
// `category` filter. Document trail entity types are kept in sync with
// AuditLog.DOCUMENT_ENTITY_TYPES (etmf-server/src/models/auditLog.model.js);
// every other AuditLog.ENTITY_TYPES value belongs to the Application trail.
const DOCUMENT_ENTITY_TYPES = [
  { value: 'StudyProtocol', label: 'Protocol' },
  { value: 'Section', label: 'Protocol Section' },
  { value: 'ScheduleOfActivities', label: 'Schedule of Activities' },
  { value: 'SummaryOfChanges', label: 'Summary of Changes' },
  { value: 'Amendment', label: 'Amendment' },
  { value: 'ClinicalIntake', label: 'Protocol Intake Form' },
  { value: 'TemplateLanguage', label: 'Template Language' },
  { value: 'SAP', label: 'SAP Section' },
  { value: 'SapUsdm', label: 'SAP (USDM)' },
  { value: 'SapArs', label: 'SAP ARS' },
  { value: 'SapShells', label: 'SAP Shells' },
  { value: 'SapTitlePage', label: 'SAP Title Page' },
  { value: 'ICF', label: 'ICF Section' },
  { value: 'ICFLanguage', label: 'ICF Language' },
  { value: 'ICFSectionTranslation', label: 'ICF Translation' },
  ...(IND_ENABLED ? [{ value: 'IND', label: 'IND Section' }, { value: 'IndIntake', label: 'IND Intake' }] : []),
  { value: 'Document', label: 'Uploaded Document' },
  { value: 'DocumentWorkflow', label: 'Document Workflow' },
  { value: 'ApprovalRequest', label: 'Approval Request' },
  { value: 'SourceDocument', label: 'Source Document' },
];

const APPLICATION_ENTITY_TYPES = [
  'Auth', 'Project', 'StudyTeam', 'StudyOpenPreference', 'StudyDesignSelection',
  'StudyRegistration', 'AdministrativeInfo', 'ProtocolRegistration', 'UsdmInstance',
  'CanonicalDataset', 'S9Workspace', 'S9SoaVersion', 'FeasibilityQuestionnaire',
  'FeasibilityCountryReport', 'FeasibilitySiteReport', 'SubmitResults', 'KnowledgeGraph',
  'DocumentMapEntry', 'PolicyRulebookFile', 'DesignStudioSession', 'LegalHold', 'RetentionPolicy',
].map((value) => ({ value, label: value }));

const TRAILS = {
  document: { label: 'Document Audit Trail', entityTypes: DOCUMENT_ENTITY_TYPES, exportName: 'document-audit-trail.csv' },
  application: { label: 'Application Audit Trail', entityTypes: APPLICATION_ENTITY_TYPES, exportName: 'application-audit-trail.csv' },
};

function entityTypeLabel(entityType) {
  const match = [...DOCUMENT_ENTITY_TYPES, ...APPLICATION_ENTITY_TYPES].find((t) => t.value === entityType);
  return match ? match.label : entityType;
}

const EMPTY_FILTERS = { entityType: '', action: '', dateFrom: '', dateTo: '', projectId: '', version: '' };
// Kept in sync with AuditLog.ACTIONS (auditLog.model.js). Note this is
// narrower than Standard_Audit_Trail_Actions.pdf's 20-action vocabulary
// (e.g. "Regenerated (AI)", "Comments Incorporated", "Electronically Signed")
// — the backend doesn't emit those distinct action strings yet for
// StudyProtocol/ICF/SAP (they're logged as generic create/update via
// auditablePlugin), so this list isn't extended to match the PDF until that
// backend work happens — listing options the data can never match would be
// a filter that silently returns nothing.
const ACTIONS = ['create', 'update', 'delete', 'status_change', 'approve', 'reject', 'reassign', 'login', 'login_failed', 'register', 'logout', 'archive', 'upload'];

// Only these entity types carry a whole-document decimal version today
// (StudyProtocol/ICF/SAP/SapUsdm models — see studyProtocol.model.js,
// icf.model.js, sap.model.js, sapUsdm.model.js — plus 'Section', which is
// docuementEditor.model.js's DocumentEditor collection: the Protocol editor,
// audited under entityType 'Section' via explicit recordAuditEvent calls in
// documentEditor.controller.js rather than the auditablePlugin pattern).
// Decimal-as-integer: 0.9 -> 0.10, not a float. SapArs/SapShells are
// machine-derived, one-shot artifacts (re-derived wholesale, not
// authored/versioned), so they're deliberately excluded.
// 'Document' carries the protocol version on workflow checkpoint entries ("Protocol
// version 0.6 → 0.7 (sent for review)", services/protocolVersion.service.js).
const VERSIONED_ENTITY_TYPES = ['StudyProtocol', 'ICF', 'SAP', 'SapUsdm', 'Section', 'IND', 'Document'];

// The 15 stages from Standard_Version_Labels.pdf, in order. The underlying
// version field is still a plain incrementing counter (fixed-stage state
// machine deferred), but the filter can still offer the standard's real
// version/label pairs rather than free text. Two labels intentionally share
// one version (0.3, 0.7) per the PDF's note that a review round alone
// doesn't bump the version.
const VERSION_STAGES = [
  { version: '0.1', label: 'Initial Draft — AI-Generated' },
  { version: '0.1', label: 'Draft in Progress' },
  { version: '0.2', label: 'Internal Review / Validation' },
  { version: '0.3', label: 'Sent for Team Review — Draft 1' },
  { version: '0.3', label: 'Team Review Complete — Draft 1' },
  { version: '0.4', label: 'Incorporating Comments — Draft 1' },
  { version: '0.5', label: 'Updated Draft 1 — Redline' },
  { version: '0.6', label: 'Updated Draft 2 — Clean' },
  { version: '0.7', label: 'Sent for Team Review — Draft 2' },
  { version: '0.7', label: 'Team Review Complete — Draft 2' },
  { version: '0.8', label: 'Incorporating Comments — Draft 2' },
  { version: '0.9', label: 'Updated Draft 2 — Redline' },
  { version: '0.10', label: 'Final Draft — Clean' },
  { version: '0.11', label: 'Sent for Approval' },
  { version: '1.0', label: 'Approved / Final' },
];

// entry.context (newer entries) is the whole record's { title, version,
// provenance } at event time; before/after on an update only hold the fields
// that changed, so older entries without context fall back to those.
function versionLabel(entry) {
  if (!VERSIONED_ENTITY_TYPES.includes(entry.entityType)) return '—';
  const version = entry.context?.version || entry.after?.version || entry.before?.version;
  if (!version || (version.major === undefined && version.minor === undefined)) return '—';
  return `${version.major ?? 0}.${version.minor ?? 1}`;
}

// Only ICF/SAP/SapUsdm/Section set this today (see icf.model.js/sap.model.js/
// sapUsdm.model.js/docuementEditor.model.js's provenance field) — 'aidrafted'
// only from the real AI-generation call sites (ICFEditor.jsx/SAPEditor.jsx/
// SAPEditorUsdm.jsx/DocumentEditor.jsx's generate/regenerate handlers),
// everything else defaults to 'edited'. StudyProtocol has no AI-generation
// flow, so it never carries this field.
//
// 'reviewed' is kept here as a display-only mapping for history: it was the
// field's value (a mislabeling — Standard_Audit_Trail_Actions.pdf reserves
// "Reviewed" for a no-content-change evaluation) until it was renamed to
// 'edited'. AuditLog entries are immutable, so old entries will carry
// 'reviewed' forever; this keeps them rendering sensibly rather than
// falling through to '—'. No new entry will ever set 'reviewed' again.
const PROVENANCE_TAG = {
  aidrafted: { label: 'AI-drafted', color: '#92400e', bg: '#fef3c7' },
  edited: { label: 'Edited', color: '#713f12', bg: '#fef9c3' },
  reviewed: { label: 'Edited', color: '#713f12', bg: '#fef9c3' },
};

function provenanceTag(entry) {
  if (entry.context) return PROVENANCE_TAG[entry.context.provenance] || null;
  // Older entries: a record saved with no output (e.g. an empty section
  // created by "initialize") only carries the schema default 'edited' — no
  // one edited anything, so show no tag.
  const snapshot = entry.after || entry.before || {};
  if ('output' in snapshot && !String(snapshot.output || '').trim()) return null;
  const provenance = entry.after?.provenance || entry.before?.provenance;
  return PROVENANCE_TAG[provenance] || null;
}

// StudyProtocol is one document per protocol (no per-section split); ICF/SAP
// are stored as one row per section/title (see icf.model.js/sap.model.js).
function sectionLabel(entry) {
  if (entry.entityType === 'StudyProtocol') return 'Full Document';
  if (entry.entityType === 'SourceDocument') return entry.after?.fileName || '—';
  if (['ICF', 'SAP', 'SapUsdm', 'Section', 'IND'].includes(entry.entityType)) {
    return entry.context?.title || entry.after?.title || entry.before?.title || '—';
  }
  return '—';
}

// Groups the currently-loaded page of entries by version, oldest first,
// mirroring the mock's timeline view. Entries without a version (most entity
// types today) collect under a single "Unversioned" bucket rather than one
// group per entry.
function buildVersionGroups(entries) {
  const asc = [...entries].reverse();
  const groups = [];
  let current = null;
  for (const entry of asc) {
    const key = versionLabel(entry);
    if (!current || current.key !== key) {
      current = { key, rows: [] };
      groups.push(current);
    }
    current.rows.push(entry);
  }
  return groups;
}

function actorLabel(actor) {
  if (!actor) return 'Unknown';
  const u = actor.userId;
  if (u && typeof u === 'object') {
    const name = [u.firstName, u.lastName].filter(Boolean).join(' ');
    return name || u.email || actor.role || 'Unknown';
  }
  return actor.role || 'Unknown';
}

function DiffPanel({ before, after }) {
  const keys = Array.from(new Set([...Object.keys(before || {}), ...Object.keys(after || {})]));
  if (!keys.length) return <Typography color="text.secondary">No field-level changes recorded.</Typography>;
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Field</TableCell>
          <TableCell>Before</TableCell>
          <TableCell>After</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {keys.map((key) => (
          <TableRow key={key}>
            <TableCell sx={{ fontWeight: 600 }}>{key}</TableCell>
            <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap' }}>
              {JSON.stringify(before?.[key], null, 2) ?? '—'}
            </TableCell>
            <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap' }}>
              {JSON.stringify(after?.[key], null, 2) ?? '—'}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

const AuditTrail = () => {
  const { isAdmin, loading: authLoading } = useAuth();

  const [trail, setTrail] = useState('document');
  const isDocumentTrail = trail === 'document';
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [viewMode, setViewMode] = useState('table');

  // Study and date range carry across trails; entity type, action and version
  // are trail-specific, so they reset on switch.
  const handleTrailChange = (_, next) => {
    if (!next || next === trail) return;
    setTrail(next);
    setPage(0);
    setViewMode('table');
    setFilters((f) => ({ ...EMPTY_FILTERS, projectId: f.projectId, dateFrom: f.dateFrom, dateTo: f.dateTo }));
  };
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [result, setResult] = useState({ entries: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);

  // Studies are grouped under their parent drug/project, mirroring the
  // Drug > Study picker in components/layout/StudySwitcher.jsx — "projectId"
  // in this app always means one specific study, never the drug it rolls up to.
  const [drugs, setDrugs] = useState([]);
  const [studiesByDrug, setStudiesByDrug] = useState({});

  const allStudies = Object.values(studiesByDrug).flat();

  const projectLabel = useCallback((id) => {
    const p = allStudies.find((study) => study._id === id);
    if (!p) return null;
    const primary = projectListPrimaryLabel(p);
    const secondary = projectListSecondaryLabel(p);
    return secondary ? `${primary} — ${secondary}` : primary;
  }, [allStudies]);

  const activeFilters = {
    ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)),
    category: trail,
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getAuditLogs(activeFilters, { page: page + 1, limit: rowsPerPage });
      setResult(res.data);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [JSON.stringify(activeFilters), page, rowsPerPage]);

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin, load]);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    drugService.getAllDrugs()
      .then((drugList) => {
        if (cancelled) return;
        setDrugs(drugList || []);
        (drugList || []).forEach((drug) => {
          projectService.listProjects({ drug: drug._id })
            .then((res) => { if (!cancelled) setStudiesByDrug((prev) => ({ ...prev, [drug._id]: res.data || [] })); })
            .catch((err) => console.error('Error loading studies for drug', drug._id, err));
        });
      })
      .catch((err) => console.error('Error loading drugs for audit filter:', err));
    return () => { cancelled = true; };
  }, [isAdmin]);

  const openDetail = async (id) => {
    try {
      const res = await getAuditLogById(id);
      setSelected(res.data);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load entry detail');
    }
  };

  const handleExport = async () => {
    try {
      const blob = await exportAuditLogsCsv(activeFilters);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = TRAILS[trail].exportName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to export audit logs');
    }
  };

  if (authLoading) return null;

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">You do not have permission to view the audit trail.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Audit Trail"
        actions={
          <Tooltip title="Export CSV">
            <IconButton onClick={handleExport} sx={{ border: '1px solid', borderColor: 'divider' }}>
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        }
      />

      <Tabs value={trail} onChange={handleTrailChange} sx={{ mb: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Tab value="document" label={TRAILS.document.label} />
        <Tab value="application" label={TRAILS.application.label} />
      </Tabs>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {isDocumentTrail
          ? 'Protocol, SAP, ICF and their sections, amendments, workflow and approvals, plus the source documents they were generated from.'
          : 'Sign-ins, studies, study team, settings, registrations and other application activity.'}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          {/* Order mirrors audit-trail-preview.html's filter row: Study/Project,
              Entity Type, Version (highlighted as the new filter), Action. */}
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Study / Project</InputLabel>
              <Select
                label="Study / Project"
                value={filters.projectId}
                onChange={(e) => {
                  const projectId = e.target.value;
                  setPage(0);
                  setFilters((f) => ({ ...f, projectId }));
                }}
              >
                <MenuItem value="">All studies</MenuItem>
                {drugs.flatMap((drug) => {
                  const studies = studiesByDrug[drug._id] || [];
                  return [
                    <ListSubheader key={`drug-${drug._id}`}>{drug.name}</ListSubheader>,
                    ...(studies.length
                      ? studies.map((study) => (
                          <MenuItem key={study._id} value={study._id} sx={{ pl: 4 }}>
                            {projectListPrimaryLabel(study)}
                            {projectListSecondaryLabel(study) ? ` — ${projectListSecondaryLabel(study)}` : ''}
                          </MenuItem>
                        ))
                      : [<MenuItem key={`empty-${drug._id}`} value="" disabled sx={{ pl: 4, fontStyle: 'italic' }}>No studies yet</MenuItem>])
                  ];
                })}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>{isDocumentTrail ? 'Document Type' : 'Entity Type'}</InputLabel>
              <Select
                label={isDocumentTrail ? 'Document Type' : 'Entity Type'}
                value={filters.entityType}
                onChange={(e) => {
                  const entityType = e.target.value;
                  setPage(0);
                  setFilters((f) => ({ ...f, entityType }));
                }}
              >
                <MenuItem value="">All</MenuItem>
                {TRAILS[trail].entityTypes.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          {isDocumentTrail && (
          <Grid item xs={12} sm={4}>
            <FormControl
              fullWidth
              size="small"
              sx={{
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
                '& .MuiInputLabel-root': { color: 'primary.main', fontWeight: 600 }
              }}
            >
              <InputLabel>Version</InputLabel>
              <Select
                label="Version"
                value={filters.version}
                onChange={(e) => { setPage(0); setFilters((f) => ({ ...f, version: e.target.value })); }}
              >
                <MenuItem value="">All versions</MenuItem>
                {VERSION_STAGES.map((stage, i) => (
                  <MenuItem key={i} value={stage.version}>{stage.version} — {stage.label}</MenuItem>
                ))}
              </Select>
              <Typography variant="caption" sx={{ mt: 0.5, px: 0.5, color: 'primary.main' }}>
                Only applies to Protocol / ICF / SAP entries
              </Typography>
            </FormControl>
          </Grid>
          )}
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Action</InputLabel>
              <Select
                label="Action"
                value={filters.action}
                onChange={(e) => { setPage(0); setFilters((f) => ({ ...f, action: e.target.value })); }}
              >
                <MenuItem value="">All</MenuItem>
                {ACTIONS.map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          {/* Below this line: extra filters not present in the mock, kept since
              they're real search capability already in use (date range). */}
          <Grid item xs={12} sm={2}>
            <TextField
              label="From"
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={filters.dateFrom}
              onChange={(e) => { setPage(0); setFilters((f) => ({ ...f, dateFrom: e.target.value })); }}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              label="To"
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={filters.dateTo}
              onChange={(e) => { setPage(0); setFilters((f) => ({ ...f, dateTo: e.target.value })); }}
            />
          </Grid>
        </Grid>
      </Paper>

      {isDocumentTrail && (
        <ToggleButtonGroup
          size="small"
          exclusive
          value={viewMode}
          onChange={(_, next) => next && setViewMode(next)}
          sx={{ mb: 2 }}
        >
          <ToggleButton value="table">Table view</ToggleButton>
          <ToggleButton value="timeline">Timeline view (grouped by version)</ToggleButton>
        </ToggleButtonGroup>
      )}

      <Paper variant="outlined">
        {loading && result.entries.length === 0 ? (
          <LoadingState label="Loading audit logs…" />
        ) : !loading && result.entries.length === 0 ? (
          <EmptyState compact title="No audit log entries found" description="Try adjusting your filters above." />
        ) : viewMode === 'timeline' ? (
        <Box sx={{ p: 2 }}>
          {buildVersionGroups(result.entries).map((group, gi) => (
            <Box key={gi} sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ fontFamily: 'monospace', color: group.key === '—' ? 'text.secondary' : 'primary.main', mb: 1 }}>
                {group.key === '—' ? 'Unversioned' : `Version ${group.key}`}
              </Typography>
              {group.rows.map((entry) => (
                <Box
                  key={entry._id}
                  onClick={() => openDetail(entry._id)}
                  sx={{
                    display: 'flex', gap: 1.5, alignItems: 'baseline', py: 0.5, px: 1,
                    cursor: 'pointer', borderRadius: 1, fontSize: 13,
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 160, fontFamily: 'monospace' }}>
                    {new Date(entry.createdAt).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 120 }}>
                    {actorLabel(entry.actor)}
                  </Typography>
                  <Chip size="small" label={entry.action} />
                  <Typography variant="body2" color="text.secondary">{entry.summary}</Typography>
                </Box>
              ))}
            </Box>
          ))}
        </Box>
        ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Timestamp</TableCell>
                <TableCell>Study</TableCell>
                <TableCell>{isDocumentTrail ? 'Document' : 'Entity Type'}</TableCell>
                {isDocumentTrail && <TableCell>Version</TableCell>}
                {isDocumentTrail && <TableCell>Section / File</TableCell>}
                <TableCell>Action</TableCell>
                <TableCell>User</TableCell>
                {isDocumentTrail && <TableCell>Provenance</TableCell>}
                <TableCell>Summary</TableCell>
                <TableCell align="right">Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {result.entries.map((entry) => (
                <TableRow key={entry._id} hover>
                  <TableCell>{new Date(entry.createdAt).toLocaleString()}</TableCell>
                  <TableCell>{projectLabel(entry.projectId) || (entry.projectId ? '—' : '')}</TableCell>
                  <TableCell>{entityTypeLabel(entry.entityType)}</TableCell>
                  {isDocumentTrail && <TableCell>{versionLabel(entry)}</TableCell>}
                  {isDocumentTrail && <TableCell>{sectionLabel(entry)}</TableCell>}
                  <TableCell><Chip size="small" label={entry.action} /></TableCell>
                  <TableCell>{actorLabel(entry.actor)}</TableCell>
                  {isDocumentTrail && (
                    <TableCell>
                      {provenanceTag(entry) ? (
                        <Chip
                          size="small"
                          label={provenanceTag(entry).label}
                          sx={{ bgcolor: provenanceTag(entry).bg, color: provenanceTag(entry).color, fontWeight: 600 }}
                        />
                      ) : '—'}
                    </TableCell>
                  )}
                  <TableCell>{entry.summary}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openDetail(entry._id)}>
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        )}
        <TablePagination
          component="div"
          count={result.total || 0}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="md" fullWidth>
        <DialogTitle>Audit Entry Detail</DialogTitle>
        <DialogContent>
          {selected && (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {entityTypeLabel(selected.entityType)} · {selected.action} · {new Date(selected.createdAt).toLocaleString()} · {actorLabel(selected.actor)}
              </Typography>
              {(selected.projectId || selected.documentId || selected.entityId || selected.ipAddress) && (
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1, fontFamily: 'monospace' }}>
                  {selected.projectId && <>Study: {projectLabel(selected.projectId) || selected.projectId}<br /></>}
                  {selected.documentId && <>Document ID: {selected.documentId}<br /></>}
                  {selected.entityId && <>Entity ID: {selected.entityId}<br /></>}
                  {selected.ipAddress && <>IP / Workstation: {selected.ipAddress}</>}
                </Typography>
              )}
              <Typography sx={{ mb: 2 }}>{selected.summary}</Typography>
              <DiffPanel before={selected.before} after={selected.after} />
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AuditTrail;
